<script context="module">
  /* eslint-disable camelcase, max-lines-per-function */
  import { get } from "svelte/store";
  import { VIDEOS } from "../consts";
  import Video, { toggleActiveVideo } from "@components/Video";
  import { cueConversationVideo } from "@lib/client/video";
  import { logVideoStart, logVideoComplete } from "@services/analytics";
  import { featureEnabled } from "@stores/features";
  import {
    allowedQuestions,
    displayAllowedQuestions
  } from "@stores/features/allowed-questions";
  import { tests } from "@stores/features/hide-controls";
  import { displayLinks, links } from "@stores/features/links";
  import { userHasPaid } from "@stores/features/paywall";
  // eslint-disable-next-line max-len
  import {
    displaySuggestedQuestions,
    suggestedQuestions
  } from "@stores/features/suggested-questions";
  import {
    activeVideo,
    conversationVideo
  } from "@stores/global";

  /**
   * Cue up conversation video and play it through
   *
   * @param {object} options
   * @param {Function} options.onEnd callback after video ended
   * @param {Function} options.onPlayback callback when video ready to play
   * @param {string} options.src Video src
   * @param {string} options.title Video title (usually media id)
   * @param {HTMLElement} options.video Video element
   * @returns {object}
   */
  export function cueConversationAndPlay({
    onEnd,
    onPlayback,
    src,
    title,
    video
  }) {
    return cueConversationVideo(
      video,
      src,
      function beforePlaying() {
        let ended = false;

        tests.update(old => ({ ...old, videoend: false }));

        video.onended = function conversationVideoOnEnded() {
          if (!ended) {
            ended = true;
            tests.update(old => ({ ...old, videoend: true }));
            logVideoComplete(title, video.duration);
          }
        };

        video.onloadedmetadata = function conversationVideoOnLoadMetadata() {
          logVideoStart(title, video.duration);
        };

        /**
         * If video has ended, trigger ended event
         * This is due to iOS occassionally not firing
         * ended event
         */
        function conversationVideoOnTimeUpdate() {
          if (
            (!ended && this.duration - this.currentTime < 0.2)) {
            this.dispatchEvent(new Event("ended"));
            video.removeEventListener(
              "timeupdate",
              conversationVideoOnTimeUpdate
            );
          }
        }

        video.addEventListener("timeupdate", conversationVideoOnTimeUpdate);
      }).andPlay(
      () => {
        if (typeof onPlayback === "function") {
          onPlayback();
        }

        toggleActiveVideo().andPlay()
          .atEnd(() => {
            toggleActiveVideo().andPlay();

            // Links trumps suggested questions
            if (!featureEnabled("links") || get(links).length === 0) {
              // Allowed questions are only visible in pre-auth
              // context of a paid experience
              if (
                featureEnabled("allowed-questions") &&
                featureEnabled("paywall") &&
                !get(userHasPaid) &&
                get(allowedQuestions) &&
                get(allowedQuestions).length
              ) {
                // eslint-disable-next-line max-nested-callbacks
                displayAllowedQuestions.update(() => true);
              }

              // Suggested questions can be enabled either post-auth
              // context of paid experience or in a regular experience
              if (
                featureEnabled("suggested-questions") &&
                get(suggestedQuestions) &&
                get(suggestedQuestions).length &&
                (featureEnabled("paywall") ? get(userHasPaid) : true)
              ) {
                // eslint-disable-next-line max-nested-callbacks
                displaySuggestedQuestions.update(() => true);
              }
            }

            if (featureEnabled("links") && get(links).length) {
              // eslint-disable-next-line max-nested-callbacks
              displayLinks.update(() => true);
            }

            if (typeof onEnd === "function") {
              onEnd();
            }
          });
      }
    );
  }
</script>

<script>
  export let src;

  $: active = $activeVideo === VIDEOS.CONVERSATION;
</script>

<Video {active} muted={false} bind:element={$conversationVideo} {src} type={VIDEOS.CONVERSATION} />
